<template>
  <validation-observer
    v-slot="{ handleSubmit }"
    ref="observer"
    tag="div"
    class="modern-card modal-card banner-form"
  >
      <b-loading :is-full-page="false" :active="isLoading" :can-cancel="false"></b-loading>

      <header class="modern-card__header">
        {{ isUpdate ? 'Edit banner' : 'Add banner' }}

        <b-button
          class="modern-card__close-button"
          aria-label="close"
          icon-right="close"
          @click="$emit('close')"
        />
      </header>

      <section class="modern-card__body">
        <form class="modern-modal-form">
          <ModernField
            v-model="form.name"
            name="name"
            label="Name*"
            :rules="{
              required: true,
              max: 50,
              latin_characters_digits_spaces: true
            }"
          >
            <ModernTextInput
              v-model="form.name"
              :disabled="isUpdate"
              maxlength="50"
            />
          </ModernField>

          <template v-for="size in bannerSizes">
            <ModernField
              :value="imageSources[size.id]"
              :key="size.id"
              :name="`imageUrl_${size.id}`"
              :label="'Image ' + size.id + '*'"
              rules="required"
            >
              <div class="drag-drop-container">
                <ModernDragDropInput
                  accept="image/jpeg, image/png, image/gif"
                  :files.sync="selectedFiles[size.id]"
                  @select="(files) => onFileSelect(files, size.id)"
                >
                  <template #filesContent>
                    <div class="drag-drop-image-wrapper">
                      <img
                        class="drag-drop-image"
                        :src="imageSources[size.id]"
                        :alt="imageAlts[size.id]"
                      >
                    </div>
                  </template>
                </ModernDragDropInput>
              </div>
            </ModernField>
          </template>

          <ModernField
            v-model="form.userTypeId"
            label="User*"
            rules="required"
          >
            <b-select
              v-model.number="form.userTypeId"
              size="is-medium"
              expanded
            >
              <option
                v-for="(option, idx) in userTypes"
                :key="idx"
                :value="option.id"
              >
                <template v-if="option.isCustomerType">
                  Customer:
                </template>
                {{ option.value }}
              </option>
            </b-select>
          </ModernField>

          <ModernField
            v-model="form.themeId"
            label="Theme*"
            rules="required"
          >
            <b-select
              v-model="form.themeId"
              :disabled="!form.userTypeId"
              size="is-medium"
              expanded
            >
              <option
                v-for="item in themeOptions"
                v-bind:key="item.id"
                :value="item.id"
              >
                {{ item.name }}
              </option>
            </b-select>
          </ModernField>
        </form>
      </section>

      <footer class="modern-card__footer">
          <button
            type="button"
            class="button is-success outlined"
            @click="$emit('close')"
          >
            Cancel
          </button>

          <button
            type="button"
            class="button is-success"
            @click="handleSubmit(submit)"
          >
            {{ isUpdate ? 'Update' : 'Create' }}
          </button>
        </footer>
  </validation-observer>
</template>

<script>
import ModernField from '@/components/Form/Field/ModernField.vue';
import ModernTextInput from '@/components/Form/Input/ModernTextInput.vue';
import {
  create,
  read,
  update,
  uploadImages, validateImage,
} from '@/api/banner';
import { get } from 'lodash';
import { mapGetters } from 'vuex';
import ModernDragDropInput from '@/components/Form/Input/ModernDragDropInput.vue';
import getEntityChangedFields from '@/utils/getEntityChangedFields';
import preloadImage from '@/utils/preloadImage';
import Banner from '@/entity/Banner/Banner';

export default {
  name: 'BannerForm',
  components: {
    ModernTextInput,
    ModernField,
    ModernDragDropInput,
  },

  props: {
    id: {
      type: Number,
      default: null,
    },
    themes: {
      type: Array,
      default: () => ([]),
    },
  },

  data() {
    const form = new Banner();
    form.images = [];

    const selectedFiles = {};
    const imageFiles = {};
    const bannerSizes = [
      { id: '930x180', width: 930, height: 180 },
      { id: '728x90', width: 728, height: 90 },
      { id: '400x400', width: 400, height: 400 },
      { id: '300x600', width: 300, height: 600 },
    ];

    bannerSizes.forEach((bannerSize) => {
      selectedFiles[bannerSize.id] = [];
      imageFiles[bannerSize.id] = null;
      form.images.push({
        sizeKey: bannerSize.id,
        filename: null,
        url: null,
      });
    });

    return {
      isLoading: false,
      entity: new Banner(),
      form,
      selectedFiles,
      imageFiles,
      bannerSizes,
    };
  },

  async mounted() {
    this.isLoading = true;

    try {
      if (this.isUpdate) {
        const entityResponse = await read(this.id);
        this.entity.loadData(entityResponse.data);
        this.form.loadData(entityResponse.data);
        this.form.images.forEach((image) => {
          this.selectedFiles[image.sizeKey] = image.url ? [image.url] : [];
        });
      }
    } catch (error) {
      this.$buefy.snackbar.open({
        message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
        actionText: ' ',
        position: 'is-top',
        indefinite: true,
        type: 'is-danger',
      });
      this.$emit('close');
    }

    if (this.isUpdate) {
      try {
        const promises = [];
        this.form.images.forEach((image) => {
          promises.push(preloadImage(image.url));
        });

        await Promise.all(promises);
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load image',
          type: 'is-danger',
          duration: 5000,
        });
      }
    }

    this.isLoading = false;
  },

  watch: {
    'form.userTypeId': function formUserTypeIdWatcher(value, oldValue) {
      if (oldValue !== undefined) {
        this.form.themeId = null;
      }
    },
  },

  computed: {
    ...mapGetters('constant', ['userTypes']),
    themeOptions() {
      return this.themes.filter((item) => item.userType.id === this.form.userTypeId);
    },
    isUpdate() {
      return this.id !== null;
    },
    imageSources() {
      const imageSources = {};

      Object.keys(this.imageFiles).forEach((key) => {
        let src;

        if (this.imageFiles[key]) {
          src = URL.createObjectURL(this.imageFiles[key]);
        } else {
          const formImage = this.getFormImageBySizeKey(key);
          src = formImage.url;
        }

        imageSources[key] = src;
      });

      return imageSources;
    },
    imageAlts() {
      const imageAlts = {};

      Object.keys(this.imageFiles).forEach((key) => {
        let alt = null;

        if (this.imageFiles[key]) {
          alt = this.imageFiles[key].name;
        }

        imageAlts[key] = alt;
      });

      return imageAlts;
    },
  },

  methods: {
    getFormImageBySizeKey(key) {
      return this.form.images.find((item) => item.sizeKey === key);
    },
    async submitUploadImages() {
      const formData = new FormData();
      if (!this.isUpdate) {
        formData.set('name', this.form.name);
      }

      let i = 0;
      Object.keys(this.imageFiles).forEach((key) => {
        const sourceFile = this.imageFiles[key];
        if (sourceFile) {
          const uploadFile = new File([sourceFile], 'uploadImage', { type: 'image/png' });
          const { width, height } = this.bannerSizes.find((size) => size.id === key);
          formData.set(`uploadImages[${i}][width]`, width);
          formData.set(`uploadImages[${i}][height]`, height);
          formData.set(`uploadImages[${i}][image]`, uploadFile);
          i += 1;
        }
      });

      if (i > 0) {
        const response = await uploadImages(this.isUpdate ? this.id : 'null', formData);
        response.data.forEach((item) => {
          const formImage = this.getFormImageBySizeKey(item.sizeKey);
          formImage.filename = item.filename;
          formImage.url = item.url;
        });
      }
    },
    async submit() {
      this.isLoading = true;

      if (this.isValidName()) {
        try {
          await this.submitUploadImages(); // mutates this.form.images

          const payload = getEntityChangedFields(this.entity, this.form);

          const { data } = this.isUpdate
            ? await update(this.id, payload)
            : await create(payload);

          this.$buefy.toast.open({
            message: 'Update successful!',
            type: 'is-success',
            duration: 5000,
          });

          this.$emit('submit', { data });
          this.$emit('close');
        } catch (error) {
          this.$buefy.snackbar.open({
            message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
            actionText: ' ',
            position: 'is-top',
            indefinite: true,
            type: 'is-danger',
          });
        }
      }

      this.isLoading = false;
    },
    isValidName() {
      let errorMessage = null;

      if (this.isUpdate && this.entity.name !== this.form.name) {
        errorMessage = 'The name cannot be modified';
        this.$refs.observer.setErrors({ name: errorMessage });
      }

      return errorMessage === null;
    },
    isValidSelectedFile(file, key) {
      let errorMessage = null;

      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif'];
      const maxSize = 1024 * 1024;

      const extension = file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(file.type) || !allowedExtensions.includes(extension)) {
        errorMessage = 'Please upload a file in the PNG, GIF, or JPG format ';
      } else if (file.size > maxSize) {
        errorMessage = 'The uploading file cannot be more than 1 MB';
      }

      if (errorMessage) {
        const errors = {};
        errors[`imageUrl_${key}`] = errorMessage;
        this.$refs.observer.setErrors(errors);
      }

      return errorMessage === null;
    },
    async onFileSelect(files, key) {
      this.imageFiles[key] = null;
      const formImage = this.getFormImageBySizeKey(key);

      if (this.isValidSelectedFile(files[0], key)) {
        const formData = new FormData();
        const sourceFile = files[0];
        const image = new File([sourceFile], 'uploadImage', { type: 'image/png' });
        const { width, height } = this.bannerSizes.find((size) => size.id === key);
        formData.set('uploadImages[0][width]', width);
        formData.set('uploadImages[0][height]', height);
        formData.set('uploadImages[0][image]', image);

        this.isLoading = true;

        try {
          await validateImage(formData);

          formImage.filename = null;
          formImage.url = null;

          this.imageFiles[key] = sourceFile;
        } catch (error) {
          this.imageFiles[key] = null;

          if (error?.response?.status === 400) {
            const errors = {};
            errors[`imageUrl_${key}`] = error?.response?.data?.message || 'This field is invalid';
            this.$refs.observer.setErrors(errors);
          } else {
            this.$buefy.snackbar.open({
              message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
              actionText: ' ',
              position: 'is-top',
              indefinite: true,
              type: 'is-danger',
            });
          }
        }

        this.isLoading = false;
      } else {
        this.selectedFiles[key] = formImage.url
          ? [formImage.url]
          : [];
      }
    },
  },

};
</script>

<style lang="stylus">
.banner-form
  .field .control:not(:has(textarea))
    height 48px
    input, .select, .select select
      height 100%
      font-size 1.1875rem
  .drag-drop-container
    height 215px
    & .drag-drop-image-wrapper
      pointer-events none
      height 100%
      width 100%
      & .drag-drop-image
        height 100%
        width 100%
        object-fit contain
</style>

<style scoped lang="stylus">
</style>
